<script>
import { explorerState } from "@app/store/explorerStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import {
    projectState,
    updateProjectId,
} from "@app/store/projectStore.svelte.js";
import { metadataState } from "@app/store/metadataStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { issueState } from "@app/store/issueStore.svelte.js";
import { markerState } from "@app/store/markerStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { resetHistory, insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { hideModal } from "@app/store/modalStore.svelte.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { saveLastExplorerLocation } from "@app/lib/explorerLastLocation.js";
import { v4 as uuidv4 } from "uuid";
import { toast } from "@zerodevx/svelte-toast";
import { ccprjFunc } from "@app/lib/ccprj.js";
import * as m from "@app/paraglide/messages.js";

/* Firebase */
import firebase from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import storage from "@app/configs/storage.js";

let loading = false;
const userId = firebase.auth().currentUser?.uid;
const userEmail = firebase.auth().currentUser?.email;
const teamId = authState.team ? authState.team.id : null;
const homeDbRef = db.collection("users").doc(userId).collection("storage");
const teamDbRef = teamId
    ? db.collection("teams").doc(teamId).collection("storage")
    : null;
const homeStorageBasePath = `users/${userId}/projects/`;
const teamStorageBasePath = teamId ? `teams/${teamId}/storage/` : "";

// Variable to track the first selected record
let selectedRecord = $state(null);

// Replace run with $effect to reactively update selectedRecord
$effect(() => {
    // console.log("EFFECT Updating selectedRecord based on explorerState...");
    if (explorerState.selectedRecords.length > 0) {
        selectedRecord = explorerState.records.find(
            (record) => record.id === explorerState.selectedRecords[0],
        );
    } else {
        selectedRecord = null;
    }
});

// Function to check if the selected record is a folder
let canOverwrite = $derived(selectedRecord && selectedRecord.type !== "folder");

async function createFolder() {
    if (loading) return;

    loading = true;

    try {
        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.denyCancel,
            titleText: m.explorer_display_new_folder_title(),
            text: m.explorer_display_new_folder_text(),
            showDenyButton: true,
            showCancelButton: false,
            confirmButtonText: m.explorer_display_new_folder_confirm(),
            denyButtonText: m.explorer_display_cancel_button(),
            allowOutsideClick: false,
            allowEscapeKey: false,
            inputLabel: m.explorer_display_new_folder_label(),
            inputPlaceholder: m.explorer_display_new_folder_placeholder(),
            input: "text",
        });

        if (result.isConfirmed && result.value) {
            const folderName = result.value;
            let locationId =
                explorerState.navHistory.length > 0
                    ? explorerState.navHistory[
                          explorerState.navHistory.length - 1
                      ].id
                    : null;

            const folderInfo = {
                id: uuidv4(),
                name: folderName,
                type: "folder",
                createdOn: firebase.firestore.Timestamp.fromDate(new Date()),
                updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
                locationId: locationId,
                rootDir: explorerState.rootDir,
                shared: explorerState.rootDir === "team" ? true : false,
                star: false,
                locked: false,
                description: "",
            };

            const dbRef =
                explorerState.rootDir === "team" ? teamDbRef : homeDbRef;

            await dbRef.doc(folderInfo.id).set(folderInfo);

            // Add the new folder to the records list
            explorerState.records = [folderInfo, ...explorerState.records];

            toast.push(m.explorer_display_new_folder_success_toast({ name: folderName }), {
                classes: ["toast-success"],
            });
        }
    } catch (error) {
        console.error("Error creating folder:", error);
        toast.push(m.explorer_display_new_folder_failed_toast({ error: error.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        loading = false;
    }
}

async function deleteSelectedRecords() {
    if (explorerState.selectedRecords.length === 0) return;

    // Show confirmation dialog
    const result = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.destructive,
        title: m.explorer_display_delete_records_title(),
        text: m.explorer_display_delete_records_text({ count: explorerState.selectedRecords.length }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: m.explorer_display_delete_records_confirm(),
        cancelButtonText: m.explorer_display_cancel_button(),
    });

    if (!result.isConfirmed) return;

    try {
        // Get the selected records details
        const selectedRecordObjects = explorerState.records.filter((record) =>
            explorerState.selectedRecords.includes(record.id),
        );

        // Process each selected record
        for (const record of selectedRecordObjects) {
            // Determine which database reference to use
            const dbRef = record.rootDir === "personal" ? homeDbRef : teamDbRef;

            // Skip if record is locked by someone else
            if (record.locked && record.lockedBy !== userEmail) {
                toast.push(
                    m.explorer_display_delete_skipped_locked_toast({
                        name: record.name,
                        lockedBy: record.lockedBy,
                    }),
                    {
                        classes: ["toast-warning"],
                    },
                );
                continue;
            }

            // Delete the record from Firestore
            await dbRef.doc(record.id).delete();

            // If it's a project, also delete the project file from storage
            if (
                record.type === "project" ||
                record.type === "team project" ||
                record.type === "work order"
            ) {
                const projectFilePath =
                    record.rootDir === "personal"
                        ? "users/" + userId + "/projects/" + record.id
                        : "teams/" + teamId + "/storage/" + record.id;

                if (record.rootDir === "team") {
                    // Use Cloud Function for team projects
                    await firebase
                        .functions()
                        .httpsCallable("v8DeleteProjectFile")(projectFilePath);
                } else {
                    // Use direct storage reference for personal projects
                    await storage.ref().child(projectFilePath).delete();
                }
            }
        }

        // Update the explorer state to remove the deleted records
        explorerState.records = explorerState.records.filter(
            (record) => !explorerState.selectedRecords.includes(record.id),
        );

        // Clear selection
        explorerState.selectedRecords = [];

        // Show success message
        toast.push(
            m.explorer_display_delete_success_toast({
                count: selectedRecordObjects.length,
            }),
            {
                classes: ["toast-success"],
            },
        );
    } catch (error) {
        console.error("Error deleting records:", error);
        toast.push(m.explorer_display_delete_failed_toast({ error: error.message }), {
            classes: ["toast-danger"],
        });
    }
}

async function saveProject(id = null) {
    if (loading) return;

    loading = true;

    console.log("Save Project Called");
    console.log("ID: "+id);

    try {
        // Prompt user for filename
        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.denyCancel,
            titleText: m.explorer_display_file_name_title(),
            text: m.explorer_display_file_name_text(),
            showDenyButton: true,
            showCancelButton: false,
            confirmButtonText: m.explorer_display_save_button(),
            denyButtonText: m.explorer_display_cancel_button(),
            allowOutsideClick: false,
            allowEscapeKey: false,
            input: "text",
            inputValue: projectState.name,
        });

        if (!result.isConfirmed) {
            throw new Error(m.explorer_display_save_cancelled_error());
        }

        // Update project name and refresh ID if needed
        projectState.name = result.value;
        projectState.id = uuidv4();

        if (id) {
            updateProjectId(id);
        }

        // Determine file path based on storage location
        const locationId =
            explorerState.navHistory.length > 0
                ? explorerState.navHistory[explorerState.navHistory.length - 1]
                      .id
                : null;

        const projectFilePath =
            explorerState.rootDir === "team"
                ? teamStorageBasePath + projectState.id
                : homeStorageBasePath + projectState.id;

        // Create project JSON
        const projectJson = ccprjFunc.createCcprj({
            project: JSON.parse(JSON.stringify(projectState)),
            metadata: JSON.parse(JSON.stringify(metadataState)),
            speakers: JSON.parse(JSON.stringify(speakerState)),
            issues: JSON.parse(JSON.stringify(issueState)),
            markers: JSON.parse(JSON.stringify(markerState)),
            eventGroups: JSON.parse(JSON.stringify(eventGroupState)),
            style: JSON.parse(JSON.stringify(styleState)),
        });

        projectJson.rootDir = explorerState.rootDir;
        projectJson.folderId = locationId;

        // Upload to storage
        const uploadProjectRef = storage.ref().child(projectFilePath);
        await uploadProjectRef.putString(JSON.stringify(projectJson));

        // Create project record in database
        const projectInfo = {
            id: projectState.id,
            name: projectState.name,
            type: projectState.type === "team" ? "team project" : "project",
            owner: userEmail,
            createdOn: firebase.firestore.Timestamp.fromDate(new Date()),
            updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
            locationId: locationId,
            shared: explorerState.rootDir === "team" ? true : false,
            star: false,
            description: "",
            rootDir: explorerState.rootDir,
        };

        const dbRef = explorerState.rootDir === "team" ? teamDbRef : homeDbRef;
        await dbRef.doc(projectInfo.id).set(projectInfo);

        // Update project state with location info
        projectState.folderId = locationId;
        projectState.rootDir = explorerState.rootDir;

        // Reset history and show success message
        resetHistory();
        insertHistoryAction({
            name: "save project", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                }
            ],
        });
        toast.push(m.explorer_display_save_success_toast(), {
            classes: ["toast-success"],
        });

        // Add the new project to the records list and refresh the display
        explorerState.records = [projectInfo, ...explorerState.records];

        saveLastExplorerLocation({
            userId,
            rootDir: explorerState.rootDir,
            folderId: locationId,
        });

        hideModal();
    } catch (error) {
        console.error("Error saving project:", error);
        toast.push(m.explorer_display_save_failed_toast({ error: error.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        loading = false;
    }
}

function overwriteProjectRecord() {
    try {
        console.log(selectedRecord);
        let id = selectedRecord.id;
        let description = selectedRecord.description || "";
        const preservedMeta = {
            assignedTo: selectedRecord.assignedTo ?? null,
            state: selectedRecord.state ?? null,
            locked: selectedRecord.locked || false,
            lockedBy: selectedRecord.lockedBy ?? null,
            lockedOn: selectedRecord.lockedOn ?? null,
            dueDate: selectedRecord.dueDate ?? null,
            priority: selectedRecord.priority ?? null,
            totalEvents: selectedRecord.totalEvents ?? null,
            eventGroups: selectedRecord.eventGroups ?? null,
            star: selectedRecord.star || false,
            createdOn: selectedRecord.createdOn,
        };
        if (selectedRecord.locked && selectedRecord.lockedBy !== userEmail) {
            throw new Error(m.explorer_display_overwrite_locked_error({ name: selectedRecord.name, lockedBy: selectedRecord.lockedBy }));
        }

        if (loading) {
            return;
        }

        loading = true;
        selectedRecord = undefined;
        // Use sweetalert to ask user to confirm they wish to overwrite the selected record
        Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.denyCancel,
            titleText: m.explorer_display_overwrite_title(),
            text: m.explorer_display_overwrite_text(),
            showDenyButton: true,
            showCancelButton: false,
            confirmButtonText: m.explorer_display_overwrite_confirm(),
            denyButtonText: m.explorer_display_cancel_button(),
            allowOutsideClick: false,
            allowEscapeKey: false,
        })
            .then((result) => {
                if (result.isConfirmed) {
                    //Refresh the id so it's like a save as action or overwrite action
                    updateProjectId(id);
                    let projectFilePath =
                        rootDir === "team"
                            ? teamDriveUploadBasePath + projectState.id
                            : myDriveUploadBasePath + projectState.id;
                    let uploadProjectRef = storageRef.child(projectFilePath);
                    let projectJson = ccprjFunc.createCcprj({
                        project: JSON.parse(JSON.stringify(projectState)),
                        metadata: JSON.parse(JSON.stringify(metadataState)),
                        speakers: JSON.parse(JSON.stringify(speakerState)),
                        issues: JSON.parse(JSON.stringify(issueState)),
                        markers: JSON.parse(JSON.stringify(markerState)),
                        eventGroups: JSON.parse(JSON.stringify(eventGroupState)),
                        style: JSON.parse(JSON.stringify(styleState)),
                    });
                    projectJson.rootDir = explorerState.rootDir;
                    return uploadProjectRef.putString(JSON.stringify(projectJson));
                } else {
                    throw new Error(m.explorer_display_save_cancelled_error());
                }
            })
            .then((snapshot) => {
                console.log("project file saved!", snapshot);
                let projectInfo = {
                    id: projectState.id,
                    name: projectState.name,
                    type: projectState.type === "team" ? "team project" : "project",
                    owner: email,
                    createdOn: preservedMeta.createdOn || firebase.firestore.Timestamp.fromDate(new Date()),
                    updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
                    locationId: locationId,
                    shared: rootDir === "team" ? true : false,
                    star: preservedMeta.star,
                    description,
                    assignedTo: preservedMeta.assignedTo,
                    state: preservedMeta.state,
                    locked: preservedMeta.locked,
                    lockedBy: preservedMeta.lockedBy,
                    lockedOn: preservedMeta.lockedOn,
                    dueDate: preservedMeta.dueDate,
                    priority: preservedMeta.priority,
                    totalEvents: preservedMeta.totalEvents,
                    eventGroups: preservedMeta.eventGroups,
                };

                return (rootDir === "team" ? teamRef : homeRef)
                    .doc(projectInfo.id)
                    .set(projectInfo);
            })
            .then(() => {
                //console.log("Saving Project Record...");
                projectState.folderId = locationId;
                projectState.rootDir = rootDir;
                toast.push(m.explorer_display_project_saved_toast(), {
                    classes: ["toast-success"],
                });

                resetHistory();
                insertHistoryAction({
                    name: "save project", //action name
                    eventGroup: projectState.selected,
                    snapshots: [
                        {
                            store: "eventGroupState",
                            value: JSON.stringify(eventGroupState),
                        }
                    ],
                });
                hideModal();
            })
            .catch((error) => {
                console.log("Error saving project: ", error);
                toast.push(error.message, {
                    classes: ["toast-danger"],
                });
            })
            .finally(() => {
                loading = false;
            });
        } catch(err){
            toast.push(m.explorer_display_overwrite_failed_toast({ error: err.message }), {
                classes: ["toast-danger"],
            });
            console.log(err);
            console.log(err.message);
        }
}

// Function to clear selected records
function clearSelection() {
    explorerState.selectedRecords = [];
    selectedRecord = null;
}
</script>

<div class="col-8 d-flex justify-content-end">
    <div class="btn-toolbar" role="toolbar">
        {#if explorerState.rootDir === "personal" || explorerState.rootDir === "team"}
            <div class="btn-group btn-group-sm me-2" role="group">
                {#if explorerState.context === "save" && (explorerState.rootDir === "personal" || explorerState.rootDir === "team")}
                    <!-- Add Overwrite button when a non-folder item is selected -->
                    {#if canOverwrite && explorerState.selectedRecords.length === 1}
                        <button
                            class="btn btn-warning"
                            type="button"
                            title={m.explorer_display_overwrite_selected_title()}
                            onclick={() => overwriteProjectRecord()}>
                            <i class="bi bi-arrow-repeat"></i> {m.explorer_display_overwrite_button()}
                        </button>
                    {:else}
                        <button
                            class="btn btn-primary"
                            type="button"
                            title={m.explorer_display_save_project_title()}
                            onclick={() => saveProject()}>
                            <i class="bi bi-save"></i> {m.explorer_display_save_project_button()}
                        </button>
                    {/if}
                {/if}

                {#if explorerState.view !== "kanban"}
                    <button
                        class="btn btn-outline-primary"
                        type="button"
                        title={m.explorer_display_new_folder_title_attr()}
                        onclick={createFolder}>
                        <i class="bi bi-folder-plus"></i> {m.explorer_display_new_folder_button()}
                    </button>
                {/if}

                {#if explorerState.selectedRecords.length > 0}
                    <!-- Add Clear Selection button -->
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        title={m.explorer_display_clear_selection_title()}
                        onclick={clearSelection}>
                        <i class="bi bi-x-circle"></i> {m.explorer_display_clear_selection_button()}
                    </button>
                    <button
                        class="btn btn-outline-danger"
                        type="button"
                        title={m.explorer_display_delete_selected_title()}
                        aria-label={m.explorer_display_delete_selected_aria()}
                        onclick={deleteSelectedRecords}>
                        <i class="bi bi-trash"></i>
                    </button>
                {/if}
            </div>
        {/if}

        <div class="btn-group btn-group-sm" role="group">
            <button
                onclick={() => (explorerState.view = "table")}
                class="btn {explorerState.view === 'table'
                    ? 'btn-primary'
                    : 'btn-outline-secondary'}"
                type="button"
                aria-label={m.explorer_display_list_view_aria()}>
                <i class="bi bi-list-ul"></i>
            </button>
            <button
                onclick={() => (explorerState.view = "thumbnail")}
                class="btn {explorerState.view === 'thumbnail'
                    ? 'btn-primary'
                    : 'btn-outline-secondary'}"
                type="button"
                aria-label={m.explorer_display_grid_view_aria()}>
                <i class="bi bi-grid-3x3-gap"></i>
            </button>
            {#if explorerState.context !== "save"}
                <button
                    onclick={() => (explorerState.view = "kanban")}
                    class="btn {explorerState.view === 'kanban'
                        ? 'btn-primary'
                        : 'btn-outline-secondary'}"
                    type="button"
                    aria-label={m.explorer_display_kanban_view_aria()}>
                    <i class="bi bi-kanban"></i>
                </button>
            {/if}
        </div>
    </div>
</div>
